import type { ProductStatus } from "@adpilot/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { AddedPane, type AddedPaneProps } from "./product-listing-picker/AddedPane";
import { CatalogStatusAlerts } from "./product-listing-picker/CatalogStatusAlerts";
import { ParentGroupPane } from "./product-listing-picker/ParentGroupPane";
import { ProductBrowseToolbar } from "./product-listing-picker/ProductBrowseToolbar";
import {
  type ProductInventoryGate,
  productCatalogBlocksCompletion,
} from "./product-listing-picker/product-listing-helpers";
import { SingleProductPanel } from "./product-listing-picker/SingleProductPanel";
import { SkuListingPane } from "./product-listing-picker/SkuListingPane";
import { UnifiedProductTable } from "./product-listing-picker/UnifiedProductTable";
import { useProductCatalogSync } from "./product-listing-picker/use-product-catalog-sync";
import { useProductListingBrowse } from "./product-listing-picker/use-product-listing-browse";
import { useProductResolution } from "./product-listing-picker/use-product-resolution";
import { useMountedRef, useProductSelection } from "./product-listing-picker/use-product-selection";
import { useSelectionValidation } from "./product-listing-picker/use-selection-validation";

export {
  amazonProductUrl,
  productCatalogBlocksCompletion,
  selectedProductFromListing,
  selectedSkuSetFingerprint,
} from "./product-listing-picker/product-listing-helpers";

interface ProductListingPickerProps {
  shopId: string;
  marketplaceId: string;
  selectionLimit: number;
  selected: SelectedProduct[];
  onSelectedChange: (products: SelectedProduct[]) => void;
  enterInput: string;
  onEnterInputChange: (input: string) => void;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  onCatalogBlockingChange: (blocked: boolean) => void;
  onReconnect: () => void;
  initialAvailability?: string | null;
  showAvailabilityFilter?: boolean;
  allowCatalogRefresh?: boolean;
  showInventoryRefresh?: boolean;
  blockProductList?: boolean;
  refreshInventoryOnMount?: boolean;
  showBlockedProductError?: boolean;
  onInventoryReady?: () => void;
  validateSelection?: boolean;
  adProduct?: "sp";
  unifiedTable?: boolean;
  deferEligibilityUntilContinue?: boolean;
  onProductsAdded?: (method: "search" | "enter" | "bulk", count: number) => void;
  /**
   * `single`: exactly one SKU (template portfolio, Paper TP-01). Search only — no bulk
   * entry, select-all or group checkboxes; picking a SKU replaces the selection.
   */
  selectionMode?: "single" | "multiple";
  /**
   * `ignore` keeps SKUs without known positive stock selectable (template portfolios);
   * stock is still displayed. Defaults to `require_known_stock`.
   */
  inventory?: ProductInventoryGate;
}

export function ProductListingPicker(props: ProductListingPickerProps) {
  // A Shop boundary also cancels local selection actions and query placeholders.
  return <ShopProductListingPicker key={props.shopId} {...props} />;
}

function ShopProductListingPicker({
  shopId,
  marketplaceId,
  selectionLimit,
  selected,
  onSelectedChange,
  enterInput,
  onEnterInputChange,
  invalidProducts,
  onInvalidProductsChange,
  onCatalogBlockingChange,
  onReconnect,
  initialAvailability = "IN_STOCK",
  showAvailabilityFilter = true,
  allowCatalogRefresh = true,
  showInventoryRefresh = false,
  blockProductList = false,
  refreshInventoryOnMount = false,
  showBlockedProductError = true,
  onInventoryReady,
  validateSelection = false,
  adProduct,
  unifiedTable = false,
  deferEligibilityUntilContinue = false,
  onProductsAdded,
  selectionMode = "multiple",
  inventory = "require_known_stock",
}: ProductListingPickerProps) {
  const { t } = useTranslation("campaignCreation");
  const [bulkOpen, setBulkOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const single = selectionMode === "single";
  // Hook order fixes effect order: mount guard, browse reads, selection intersection,
  // sync stream and refresh run, selection validation, then the blocking callback.
  const mounted = useMountedRef();
  const browse = useProductListingBrowse({
    shopId,
    initialAvailability,
    listingAdProduct: deferEligibilityUntilContinue ? undefined : adProduct,
    unifiedTable,
    inventory,
  });
  const { groups, listings, listingAdProduct } = browse;
  const selection = useProductSelection({
    shopId,
    selectionLimit: single ? 1 : selectionLimit,
    selected,
    onSelectedChange,
    enterInput,
    invalidProducts,
    onInvalidProductsChange,
    onProductsAdded,
    mounted,
    browse,
  });
  const sync = useProductCatalogSync({
    shopId,
    mounted,
    setRefreshStreamFailed: browse.setRefreshStreamFailed,
    onInvalidProductsChange,
    onInventoryReady,
    showInventoryRefresh,
    refreshInventoryOnMount,
  });
  const resolution = useProductResolution({
    shopId,
    enterInput,
    onEnterInputChange,
    listingAdProduct,
    mounted,
    actionVersion: selection.actionVersion,
  });
  const { resolutions } = resolution;
  const productStatuses = useMemo(() => {
    const statuses = new Map<string, ProductStatus>();
    for (const group of groups.data?.data ?? []) {
      if (group.singleListing)
        statuses.set(group.singleListing.sku, group.singleListing.productStatus);
    }
    for (const listing of listings.data?.data ?? [])
      statuses.set(listing.sku, listing.productStatus);
    for (const resolution of resolutions) {
      if (resolution.type === "resolved")
        statuses.set(resolution.listing.sku, resolution.listing.productStatus);
      if (resolution.type === "ambiguous") {
        for (const candidate of resolution.candidates)
          statuses.set(candidate.sku, candidate.productStatus);
      }
    }
    return statuses;
  }, [groups.data, listings.data, resolutions]);
  const {
    selectionValidation,
    selectionIssues,
    selectionValidationIncomplete,
    selectionUnverified,
    retryPending,
    retrySelection,
  } = useSelectionValidation({
    shopId,
    adProduct,
    inventory,
    selected,
    validateSelection,
    invalidProducts,
    onInvalidProductsChange,
    refreshStreamFailed: browse.refreshStreamFailed,
    mounted,
    selectedRef: selection.selectedRef,
    selectionRevision: selection.selectionRevision,
  });

  const catalog = groups.data?.catalog;
  const catalogBlocksCompletion = productCatalogBlocksCompletion(catalog);
  useEffect(() => {
    onCatalogBlockingChange(
      blockProductList ||
        catalogBlocksCompletion ||
        selectionUnverified ||
        selectionIssues.length > 0,
    );
  }, [
    blockProductList,
    catalogBlocksCompletion,
    onCatalogBlockingChange,
    selectionUnverified,
    selectionIssues.length,
  ]);

  const browseToolbar = (
    <ProductBrowseToolbar
      browse={browse}
      showAvailabilityFilter={showAvailabilityFilter}
      allowBulkEntry={!single}
      bulkOpen={bulkOpen}
      onBulkOpenChange={setBulkOpen}
      enterInput={enterInput}
      resolution={resolution}
      selectedBySku={selection.selectedBySku}
      bulkPending={selection.bulkPending}
      listingAdProduct={listingAdProduct}
      inventory={inventory}
      searchInputRef={searchInputRef}
      onAdd={(product) => selection.addProducts([product], "enter")}
    />
  );
  const addedPaneProps: AddedPaneProps = {
    selected: selection.selectedForDisplay,
    productStatuses,
    invalidProducts: selectionIssues,
    retryPending,
    onRetry: () => void retrySelection(),
    marketplaceId,
    max: selectionLimit,
    onRemove: selection.removeSkus,
    onClear: selection.removeAll,
  };
  return (
    <div className="flex flex-col gap-3">
      <CatalogStatusAlerts
        catalog={catalog}
        sync={sync}
        allowCatalogRefresh={allowCatalogRefresh}
        showInventoryRefresh={showInventoryRefresh}
        onReconnect={onReconnect}
      />

      {validateSelection &&
      selectionIssues.length === 0 &&
      (selectionValidation.isError ||
        (selectionValidationIncomplete &&
          !selectionIssues.some(({ reason }) => reason === "not_returned"))) &&
      selected.length > 0 ? (
        <ErrorState
          message={t("products.selectionCheck.failed")}
          onRetry={() => void retrySelection()}
        />
      ) : selectionUnverified && !selectionValidationIncomplete ? (
        <span role="status" aria-label={t("products.selectionCheck.checking")} className="sr-only">
          {t("products.selectionCheck.checking")}
        </span>
      ) : null}
      <div className="min-w-0">
        <div className="flex flex-col gap-3">
          {!unifiedTable || single ? browseToolbar : null}

          {single ? (
            <SingleProductPanel
              browse={browse}
              selection={selection}
              adProduct={adProduct}
              inventory={inventory}
              onReplace={() => searchInputRef.current?.focus()}
            />
          ) : unifiedTable ? (
            <div className="grid min-h-[430px] grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="flex min-w-0 flex-col gap-3">
                {browseToolbar}
                <UnifiedProductTable
                  browse={browse}
                  selection={selection}
                  sync={sync}
                  selectionIssues={selectionIssues}
                  blockProductList={blockProductList}
                  showInventoryRefresh={showInventoryRefresh}
                  showBlockedProductError={showBlockedProductError}
                  marketplaceId={marketplaceId}
                  inventory={inventory}
                />
              </div>
              <AddedPane compactHeader {...addedPaneProps} />
            </div>
          ) : (
            <div
              className="grid min-h-[430px] grid-cols-1 gap-4 xl:grid-cols-[minmax(240px,344fr)_minmax(380px,628fr)_minmax(280px,380fr)]"
              data-product-picker-panes
            >
              <ParentGroupPane
                browse={browse}
                selection={selection}
                marketplaceId={marketplaceId}
              />
              <SkuListingPane
                browse={browse}
                selection={selection}
                adProduct={adProduct}
                marketplaceId={marketplaceId}
                inventory={inventory}
              />
              <AddedPane {...addedPaneProps} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
